import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIsMobile } from '@vw-marketing/us-components';
import Slider from 'react-slick';

import { Arrow } from '../arrow';
import {
  FeatureAppTrackingEvent,
  useTrackingManager,
} from '../../hooks/use-tracking-manager';
import {
  onVWVideoMilestoneReachedEvent,
  onVWVideoPauseClickEvent,
  onVWVideoPlayClickEvent,
  onVWVideoPlayerStart,
  onVWVideoPlayerVideoSelect,
} from '../../tagging/tagging-helpers';
import { StyledGalleryWrapper, StyledGalleryContainer } from './styles';
import { useOrientation } from '../../hooks/use-orientation';
import { Video } from '../../typing/main';
import { VideoSlide } from '../video-slide';
import { VideoThumbnailSlide } from '../video-thumbnail-slide';

interface VideoGalleryProps {
  readonly videos?: Video[];
  readonly margin?: any;
  readonly showArrowsWithThumbnails?: boolean;
  mainContainerIsVisible: boolean;
  hideThumbnails?: boolean;
}

const SLIDE_TO_SHOW_DESKTOP = 5;

export const VideoGallery: React.FC<VideoGalleryProps> = (props) => {
  const {
    videos,
    margin,
    mainContainerIsVisible,
    hideThumbnails,
    showArrowsWithThumbnails = true,
  } = props;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const [thumbnailIndex, setThumbnailIndex] = useState(0);
  const thumbnailNavRef = useRef<Slider | null>(null);

  const isMobile = useIsMobile(767);
  const isPortrait = useOrientation();
  const slidesCount = videos && videos.length ? videos.length : 0;
  const trackingManager = useTrackingManager();

  // Tagging Events
  const handleThumbnailClick = (
    position: number,
    name?: string,
    videoInfo?: FeatureAppTrackingEvent['video'],
  ) => {
    const content = { ItemCount: slidesCount, ItemPosition: position };
    const link = {
      name: name,
      newTab: false,
    };
    onVWVideoPlayerVideoSelect(link, content, videoInfo, trackingManager);
  };

  const handleVideoPlayerStart = (
    position: number,
    videoId: string,
    name?: string,
    videoInfo?: FeatureAppTrackingEvent['video'],
  ) => {
    const content = { ItemCount: slidesCount, ItemPosition: position };
    const link = {
      name: name,
      url: `https://www.youtube.com/watch?v=${videoId}`,
      newTab: false,
    };
    trackingManager &&
      onVWVideoPlayerStart(link, content, videoInfo, trackingManager);
  };

  const handleVideoPlay = (
    position: number,
    videoId: string,
    name?: string,
    videoInfo?: FeatureAppTrackingEvent['video'],
  ) => {
    const content = { ItemCount: slidesCount, ItemPosition: position };
    const link = {
      name: name,
      url: `https://www.youtube.com/watch?v=${videoId}`,
      newTab: false,
    };
    trackingManager &&
      onVWVideoPlayClickEvent(link, content, videoInfo, trackingManager);
  };

  const handleVideoPause = (
    position: number,
    videoId: string,
    name?: string,
    videoInfo?: FeatureAppTrackingEvent['video'],
  ) => {
    const content = { ItemCount: slidesCount, ItemPosition: position };
    const link = {
      name: name,
      url: `https://www.youtube.com/watch?v=${videoId}`,
      newTab: false,
    };
    trackingManager &&
      onVWVideoPauseClickEvent(link, content, videoInfo, trackingManager);
  };

  const handleVideoMilestoneReached = (
    position: number,
    videoId: string,
    name?: string,
    milestone?: number,
    videoInfo?: FeatureAppTrackingEvent['video'],
  ) => {
    const content = { ItemCount: slidesCount, ItemPosition: position };
    const link = {
      name: name,
      url: `https://www.youtube.com/watch?v=${videoId}`,
      newTab: false,
      isDealerPage: false,
    };
    const videoInfoWithMilestone = {
      ...videoInfo,
      milestone,
    };
    trackingManager &&
      onVWVideoMilestoneReachedEvent(
        link,
        content,
        videoInfoWithMilestone,
        trackingManager,
      );
  };

  const onHandleThumbnailChange = useCallback(
    (index: number) => {
      onHandleThumbnailBeforeChange(currentSlide, index);
    },
    [currentSlide],
  );

  const onHandleThumbnailBeforeChange = (current, newIndex) => {
    let index = Number.isInteger(newIndex) ? newIndex : Math.round(newIndex);
    if (current === index || index >= slidesCount) return;
    const video = (videos?.length && videos[index]) || undefined;
    const videoInfo = {
      id: video?.videoId,
      url: `https://www.youtube.com/watch?v=${video?.videoId}`,
    };
    setCurrentSlide(index);
    handleThumbnailClick(index + 1, video?.captionText, videoInfo);
    setThumbnailIndex(index);
  };

  const settingsGalleryNav = {
    nextArrow: <Arrow isNextArrow />,
    prevArrow: <Arrow />,
    slidesToShow: isMobile ? 2.3 : SLIDE_TO_SHOW_DESKTOP,
    slidesToScroll: 1,
    arrows:
      (!isMobile &&
        showArrowsWithThumbnails &&
        slidesCount > SLIDE_TO_SHOW_DESKTOP) ||
      false,
    swipe: isMobile,
    focusOnSelect: true,
    infinite: !isMobile ? slidesCount > SLIDE_TO_SHOW_DESKTOP : false,
    beforeChange: onHandleThumbnailBeforeChange,
  };

  const getGhostSlides = () => {
    if (!videos || !videos.length) return;
    const ghostSlides: any[] = [];
    const limit =
      slidesCount <= SLIDE_TO_SHOW_DESKTOP
        ? SLIDE_TO_SHOW_DESKTOP - slidesCount
        : 0;
    for (let index = 0; index < limit; index++) {
      ghostSlides.push(<VideoThumbnailSlide isDisabled />);
    }
    return ghostSlides;
  };

  useEffect(() => {
    if (isPortrait && thumbnailIndex !== 0) {
      setPendingUpdate(true);
    }
  }, [isPortrait]);

  useEffect(() => {
    if (mainContainerIsVisible && pendingUpdate) {
      thumbnailNavRef.current?.slickGoTo(0);
      setCurrentSlide(0);
      setPendingUpdate(false);
    }
  }, [pendingUpdate, mainContainerIsVisible]);
  return (
    <>
      <link
        rel="stylesheet"
        type="text/css"
        charSet="UTF-8"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
      />
      <link
        rel="stylesheet"
        type="text/css"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
      />

      <StyledGalleryWrapper
        className="youtube-video-gallery"
        margin={margin ? margin[0] : undefined}
      >
        {videos && (
          <StyledGalleryContainer>
            <div className="video-gallery">
              {videos &&
                videos.map((video, index) => {
                  if (index !== currentSlide) return;
                  return (
                    <div key={index}>
                      <VideoSlide
                        videoId={video?.videoId}
                        title={video?.title}
                        body={video?.body}
                        linkText={video?.linkText}
                        linkUrl={video?.linkUrl}
                        disclaimers={
                          video?.disclaimers
                            ? video?.disclaimers.split(',')
                            : undefined
                        }
                        slideNumberText={
                          !hideThumbnails
                            ? `${index + 1} of ${slidesCount}`
                            : ''
                        }
                        onVideoStart={(videoInfo) =>
                          handleVideoPlayerStart(
                            index + 1,
                            video?.videoId,
                            video?.title,
                            videoInfo,
                          )
                        }
                        onVideoPlay={(videoInfo) =>
                          handleVideoPlay(
                            index + 1,
                            video?.videoId,
                            video?.title,
                            videoInfo,
                          )
                        }
                        onVideoPause={(videoInfo) =>
                          handleVideoPause(
                            index + 1,
                            video?.videoId,
                            video?.title,
                            videoInfo,
                          )
                        }
                        onVideoMilestoneReached={(milestone, videoInfo) =>
                          handleVideoMilestoneReached(
                            index + 1,
                            video?.videoId,
                            video?.title,
                            milestone,
                            videoInfo,
                          )
                        }
                      />
                    </div>
                  );
                })}
            </div>
            {!hideThumbnails && (
              <div className="video-thumbnails-slider">
                <Slider
                  {...settingsGalleryNav}
                  className="video-gallery-nav"
                  ref={thumbnailNavRef}
                >
                  {videos &&
                    videos.map((video, index) => (
                      <VideoThumbnailSlide
                        videoId={video?.videoId}
                        captionText={video?.captionText}
                        altText={video?.altText}
                        key={video.videoId}
                        onClick={onHandleThumbnailChange.bind(null, index)}
                      />
                    ))}
                  {!isMobile && getGhostSlides()}
                  {isMobile && <VideoThumbnailSlide isDisabled />}
                </Slider>
              </div>
            )}
          </StyledGalleryContainer>
        )}
      </StyledGalleryWrapper>
    </>
  );
};
